import { Alert, Box, Button, Container, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ThemeProvider, useTheme } from '@mui/material';
import { useEffect, useMemo, useState, type FormEvent, type ChangeEvent } from 'react';
import axios from 'axios';
import { useAuth } from 'src/util/authenticationProvider';
import { PrizeDto } from 'src/util/types';
import config from '../../../../config';
import PrizeList from './PrizeList';
import { useTranslation } from 'react-i18next';

export default function PrizeManagement(){
  const authContext = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [prizes, setPrizes] = useState<PrizeDto[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const {t} = useTranslation();
  const theme = useTheme();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');

  useEffect(() => {
    if (!authContext.tokens?.accessToken) {
      setPrizes([]);
      return;
    }
    setLoading(true);
    axios.get(config.apiUrl +"/api/prize/all",{
      headers: {
        'Authorization': 'Bearer ' + authContext.tokens.accessToken
      }
    })
      .then(response => {
        setPrizes(response.data.content);
      })
      .catch(err => console.log(err))
      .finally(() => setLoading(false));
  }, [authContext.tokens?.accessToken]);
  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setImageFile(file);
  };

  const handleSubmitData = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setDialogError('');
    const formData = new FormData(event.currentTarget);
    const name = (formData.get('name') ?? '').toString().trim();
    const description = (formData.get('description') ?? '').toString().trim();

    if (!imageFile) {
      setDialogError('Image is required');
      return;
    }

    try {
      setLoading(true);
      const base64Image = await fileToBase64(imageFile);

      const payload = {
        name,
        description,
        image: base64Image
      };

      const response = await axios.post(config.apiUrl + "/api/prize/create", payload, {
        headers: {
          "Authorization": "Bearer " + authContext.tokens.accessToken,
          "Content-Type": "application/json"
        }
      });

      setPrizes((prev) => [...prev, response.data.content]);
      setSuccess('Creation successful.');
      setImageFile(null);
      event.currentTarget.reset();
      setCreateDialogOpen(false);
    } catch (error) {
      console.log("Connection to Server failed.", error);
      setDialogError('Connection to Server failed.');
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteUpdate = (id: number) => {
    setPrizes((prev) => prev.filter(prizeDto => prizeDto.id !== id));
  }

  const handleEditUpdate = (updatedPrize: PrizeDto) => {
    setPrizes((prev) => prev.map((prize) => (prize.id === updatedPrize.id ? { ...prize, ...updatedPrize } : prize)));
    setSuccess(t('prizes.updateSuccess', 'Prize updated successfully.'));
    setError('');
  };

  const resetFeedback = useMemo(() => () => {
    setError('');
    setSuccess('');
  }, []);

  return(
    <>
      <ThemeProvider theme={theme}>
    <Container component="main" maxWidth="lg">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box />
          <Button variant="contained" onClick={() => { resetFeedback(); setCreateDialogOpen(true); }}>
            {t('prizes.create')}
          </Button>
        </Stack>

        {error ? <Alert severity="error">{t('prizes.error')}</Alert> : ''}
        {!createDialogOpen && success ? <Alert severity="success">{success}</Alert> : ''}

        <PrizeList
          rows={prizes}
          deleteCallback={handleDeleteUpdate}
          updateCallback={handleEditUpdate}
          loading={loading}
        />
      </Box>
    </Container>
  </ThemeProvider>

    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmitData}>
        <DialogTitle>{t('prizes.create')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="normal"
            fullWidth
            id="name"
            label={t('prizes.nameLabel')}
            name="name"
            autoComplete="name"
            required
            autoFocus
          />
          <TextField
            margin="normal"
            fullWidth
            id="description"
            label={t('prizes.descriptionLabel')}
            name="description"
            autoComplete="description"
            required
          />
          <TextField
            margin="normal"
            type="file"
            required
            fullWidth
            id="image"
            name="image"
            label={t('prizes.imageLabel')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: 'image/*' }}
            onChange={handleFileChange}
          />
          {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('general.Cancel')}</Button>
          <Button type="submit" variant="contained">{t('prizes.create')}</Button>
        </DialogActions>
      </form>
    </Dialog>
    </>
  );
}
